import React, { useCallback, useState } from 'react';

import type { DocParts } from '@aline/common';
import {
  AlineTransforms,
  assetsArrayToImageMap,
  getFileVersionDisplayName,
  PDFSerializer,
} from '@aline/common';
import { buildClientFontMap } from '@aline/fonts/client';
import { useApolloClient } from '@apollo/client/react';
import JSZip from 'jszip';
import mime from 'mime-types';
import { useSnackbar } from 'notistack';
import sanitize from 'sanitize-filename';
import { LoadingSnackbar } from 'src/components';
import { Apollo } from 'src/gql/generated';
import {
  createDocFileUrlRefresher,
  fetchFreshFilestackBlob,
} from 'src/helpers/integrations/filestack-download';
import { setFileExtension } from 'src/helpers/utils';
import { v4 as uuid4 } from 'uuid';

import type { DocumentAsset } from './file';

export function useExportDocumentsToPdf(
  documentIds: UUID[],
  options: {
    zipFileName?: string;
  } = {}
): {
  exportToPdf: () => Promise<void>;
  loading: boolean;
} {
  const client = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();
  const { zipFileName = 'aline-download' } = options;
  const [loading, setLoading] = useState<boolean>(false);
  const exportToPdf = useCallback(async () => {
    setLoading(true);
    try {
      const zip = new JSZip();
      const { data } = await client.query<
        Apollo.GetDocumentsWithAssetsQuery,
        Apollo.GetDocumentsWithAssetsQueryVariables
      >({
        fetchPolicy: 'network-only',
        query: Apollo.GetDocumentsWithAssetsDocument,
        variables: { documentIds },
      });
      const safeDocuments = data?.documents ?? [];
      await Promise.all(
        safeDocuments.map(async document => {
          const safeName = document.name || 'Untitled document';

          switch (document.latestVersion.__typename) {
            case 'DocumentVersion': {
              const { blob } = await generateBlobFromFileVersion(document.latestVersion, {
                assets: document.assets,
                client,
              });
              zip.file(sanitize(setFileExtension(safeName, 'pdf')), blob);
              break;
            }
            case 'StaticFileVersion': {
              const { blob } = await generateBlobFromFileVersion(document.latestVersion, {
                assets: document.assets,
                client,
              });
              const ext = mime.extension(document.latestVersion.fileLocation.mimetype);
              const name = typeof ext === 'string' ? setFileExtension(safeName, ext) : safeName;
              zip.file(sanitize(name), blob);
              break;
            }
          }
        })
      );

      const blob = await zip.generateAsync({ type: 'blob' });
      const dateString = new Date().toISOString();
      // Need .zip extension in file name here for FileSaver to treat as Zip file.
      saveAs(blob, sanitize(`${zipFileName}-${dateString}.zip`));
    } catch (error) {
      // Previously the failure was silent — spinner stopped, no toast. Surface
      // the underlying message so the user knows the download didn't happen.
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to download documents', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [client, documentIds, zipFileName, enqueueSnackbar]);

  return {
    exportToPdf,
    loading,
  };
}

export function useDownloadFileVersion(): {
  downloadFileVersion: (
    documentId: UUID,
    options: {
      fileVersion: DownloadFileVersion | 'latestVersion';
      exportName?: string;
    }
  ) => Promise<void>;
  loading: boolean;
} {
  const client = useApolloClient();
  const [loading, setLoading] = useState<boolean>(false);
  const downloadFileVersion = useCallback(
    async (
      documentId: UUID,
      options: {
        fileVersion: DownloadFileVersion | 'latestVersion';
        exportName?: string;
      }
    ) => {
      setLoading(true);

      const { exportName, fileVersion } = options;
      const { data } = await client.query<
        Apollo.GetDocumentWithAssetsQuery,
        Apollo.GetDocumentWithAssetsQueryVariables
      >({
        fetchPolicy: 'network-only',
        query: Apollo.GetDocumentWithAssetsDocument,
        variables: { documentId },
      });
      if (!data?.document) return;
      const { document } = data;
      const versionToDownload =
        fileVersion === 'latestVersion' ? document.latestVersion : fileVersion;
      const { blob, sanitizedName } = await generateBlobFromFileVersion(versionToDownload, {
        assets: document.assets,
        client,
        exportName,
      });

      saveAs(blob, sanitizedName);
      setLoading(false);
    },
    [client]
  );

  return {
    downloadFileVersion,
    loading,
  };
}

export function useDownloadFileVersionWithLoading() {
  const { downloadFileVersion } = useDownloadFileVersion();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const downloadFileVersionWithLoading = useCallback(
    async (...args: Parameters<typeof downloadFileVersion>) => {
      const loadSnackKey = uuid4();
      enqueueSnackbar('', {
        content: <LoadingSnackbar id={loadSnackKey} message="Downloading document..." />,
        key: loadSnackKey,
        persist: true,
      });
      const res = await downloadFileVersion(...args);
      closeSnackbar(loadSnackKey);
      return res;
    },
    [closeSnackbar, downloadFileVersion, enqueueSnackbar]
  );
  return { downloadFileVersionWithLoading };
}

type DocumentVersionWithJSON = Pick<
  Apollo.DocumentVersion,
  'createdAt' | 'documentJSON' | 'id' | 'name' | '__typename'
>;
interface FileVersionWithLocation extends Pick<
  Apollo.StaticFileVersion,
  'createdAt' | 'id' | 'name' | '__typename'
> {
  fileLocation: Pick<Apollo.DocFile, 'filestackUrl' | 'id' | 'mimetype' | '__typename'>;
}
type DownloadFileVersion = DocumentVersionWithJSON | FileVersionWithLocation;

/**
 * Output a Blob of the FileVersion.
 */
export async function generateBlobFromFileVersion(
  fileVersion: DownloadFileVersion,
  options: {
    assets: DocumentAsset[];
    client: ReturnType<typeof useApolloClient>;
    exportName?: string;
  }
): Promise<{ blob: Blob; sanitizedName: string }> {
  const { assets, client, exportName = getFileVersionDisplayName(fileVersion) } = options;

  switch (fileVersion.__typename) {
    case 'DocumentVersion': {
      const imageMap = await assetsArrayToImageMap(assets);
      const editor = AlineTransforms.createServerEditorFromJson(
        fileVersion.documentJSON as DocParts,
        {
          state: {
            editingUserId: null,
            isInSigningCeremony: false,
            isReadOnly: false,
            isSigned: false,
          },
        }
      );
      const fontMap = await buildClientFontMap(editor);
      const pdf = PDFSerializer.createPDFFromJSON(editor.children, {
        fontMap,
        imageMap,
      });

      return {
        blob: pdf.output('blob'),
        sanitizedName: sanitize(setFileExtension(exportName, 'pdf')),
      };
    }
    case 'StaticFileVersion': {
      const { mimetype } = fileVersion.fileLocation;
      const { data } = await client.query<
        Apollo.GetFileVersionDownloadUrlQuery,
        Apollo.GetFileVersionDownloadUrlQueryVariables
      >({
        fetchPolicy: 'network-only',
        query: Apollo.GetFileVersionDownloadUrlDocument,
        variables: { fileVersionId: fileVersion.id },
      });
      const downloadUrl = data?.getFileVersionDownloadURL;
      const isAlineDownload =
        typeof downloadUrl === 'string' && downloadUrl.includes('/document-download');
      const blob = isAlineDownload
        ? await fetch(downloadUrl, { credentials: 'include' }).then(res => {
            if (!res.ok) throw new Error(`Download failed with HTTP ${res.status}`);
            return res.blob();
          })
        : await fetchFreshFilestackBlob({
            refreshUrl: createDocFileUrlRefresher(client, fileVersion.fileLocation.id),
            sentryContext: { fileId: fileVersion.fileLocation.id, source: 'static-file-download' },
            url: downloadUrl ?? fileVersion.fileLocation.filestackUrl,
          });
      const effectiveMimetype = isAlineDownload ? 'application/pdf' : mimetype;
      const ext = mime.extension(effectiveMimetype);
      const name = typeof ext === 'string' ? setFileExtension(exportName, ext) : exportName;

      return { blob, sanitizedName: sanitize(name) };
    }
  }
}
